import {
  MouseEvent,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import useOnClickOutside from '@/lib/shared/hooks/use-on-click-outside';

import { clsx } from 'clsx';

import styles from './Dropdown.module.scss';

import { ArrowIcon } from '../../icons';
import { ChevronDown } from '../../icons/ChevronDown';
import { CircleButton } from '../Buttons';
import { P2 } from '../Typography';

interface IDropdown extends PropsWithChildren {
  triggerText: string;
  position: 'page' | 'modal';
  button?: (onClose: () => void) => ReactNode;
}

export const Dropdown = ({
  children,
  triggerText,
  position,
  button,
}: IDropdown) => {
  const [open, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ref = useRef<HTMLDialogElement>(null);
  const closeModal = () => {
    setIsOpen(false);
    document.body.style.overflow = 'unset';
  };
  const closeDialog = () => {
    setIsOpen(false);
    document.body.style.removeProperty('overlow');
  };
  useOnClickOutside(ref, closeModal);

  const handleOpen = (_e: MouseEvent<HTMLDivElement>) => {
    if (
      window.matchMedia('(max-width:768px)').matches ||
      position === 'modal'
    ) {
      setIsModalOpen(true);
      document.body.style.overflow = 'clip';
    } else {
      setIsOpen(true);
    }
  };
  return (
    <div className={styles.dropdown}>
      <div
        className={styles.dropdown__trigger}
        data-open={open || isModalOpen}
        onClick={handleOpen}
      >
        <P2>{triggerText}</P2>
        <ChevronDown
          className={clsx(styles.dropdown__chevron, {
            [styles['dropdown__chevron--open']]: open,
          })}
        />
      </div>
      <dialog open={open} className={styles.dropdown__content} ref={ref}>
        {open && (
          <>
            <div
              className={styles.dropdown__trigger}
              data-open={open || isModalOpen}
            >
              <P2>{triggerText}</P2>
            </div>
            {children}
            <div className={styles.dropdown__close}>
              {button && button(closeModal)}
            </div>
          </>
        )}
      </dialog>
      <DropdownModal
        open={isModalOpen}
        triggerText={triggerText}
        position={position}
        button={button}
        close={() => {
          setIsModalOpen(false);
          document.body.style.removeProperty('unset');
        }}
        dataOpen={open || isModalOpen}
      >
        {children}
      </DropdownModal>
      <dialog open={isModalOpen} className={styles.modal}>
        {isModalOpen && (
          <div className={styles.modal__content}>
            <CircleButton
              as="button"
              type="button"
              theme="mixed"
              onClick={closeDialog}
              name="return button"
              content={<ArrowIcon />}
              className={styles.modal__back}
            />
            <div
              className={styles.dropdown__trigger}
              data-open={open || isModalOpen}
            >
              <P2>{triggerText}</P2>
            </div>
            {children}
            <div className={styles.dropdown__close}>
              {button && button(closeDialog)}
            </div>
          </div>
        )}
      </dialog>
    </div>
  );
};

const DropdownModal = ({
  triggerText,
  button,
  children,
  open,
  close,
  dataOpen,
}: IDropdown & { close: () => void; open: boolean; dataOpen: boolean }) => {
  const ref = useRef<HTMLDialogElement>(null);
  useOnClickOutside(ref, () => close());

  const [element, setElement] = useState<HTMLElement>();

  useEffect(() => {
    if (!open) {
      document.body.style.overflow = '';
      setElement(undefined);
    } else {
      document.body.style.overflow = 'clip';
      const elem = document.getElementById('dialog');
      if (elem) setElement(elem);
    }
  }, [open]);
  if (!element) return null;
  return (
    open &&
    ReactDOM.createPortal(
      <dialog open={open} className={styles.modal} ref={ref}>
        {open && (
          <div className={styles.modal__content}>
            <CircleButton
              as="button"
              type="button"
              theme="mixed"
              onClick={close}
              name="return button"
              content={<ArrowIcon />}
              className={styles.modal__back}
            />
            <div className={styles.dropdown__trigger} data-open={dataOpen}>
              <P2>{triggerText}</P2>
            </div>
            {children}
            <div className={styles.dropdown__close}>
              {button && button(close)}
            </div>
          </div>
        )}
      </dialog>,
      element
    )
  );
};
